import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import BrowserPersistence from "../overrides/peregrine/util/simplePersistence";
export var CONSENT_PURPOSES = Object.freeze({
  COMMUNICATION: "communication",
  PROFILING: "profiling"
});
export var CONSENT_ERROR = Object.freeze({
  MISSING_TOKEN: "missing_token",
  UNAUTHORIZED: "unauthorized",
  INVALID: "invalid",
  AUTH_UNAVAILABLE: "auth_unavailable",
  UNREACHABLE: "unreachable",
  INVALID_RESPONSE: "invalid_response",
  NOT_CONFIGURED: "not_configured"
});
var MANAGED_PURPOSES = Object.freeze([CONSENT_PURPOSES.COMMUNICATION, CONSENT_PURPOSES.PROFILING]);
var CONSENTS_PATH = "/api/Customer/Consents";
var REQUEST_TIMEOUT_MS = 15000;
var STATUS_CODES = Object.freeze({
  400: CONSENT_ERROR.INVALID,
  401: CONSENT_ERROR.UNAUTHORIZED,
  502: CONSENT_ERROR.AUTH_UNAVAILABLE
});
export var EMPTY_CONSENTS = Object.freeze({
  [CONSENT_PURPOSES.COMMUNICATION]: false,
  [CONSENT_PURPOSES.PROFILING]: false
});
export class ConsentApiError extends Error {
  constructor(message) {
    var {
      status = null,
      code = "unknown",
      cause = null
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    super(message);
    this.name = "ConsentApiError";
    this.status = status;
    this.code = code;
    this.cause = cause;
  }
}

/**
 * Magento customer token only. A CheckPlease guest token must not be sent.
 */
export var readCustomerToken = storage => {
  var persistence = storage || new BrowserPersistence();
  var token = persistence.getItem("signin_token");
  if (typeof token !== "string" || token.trim() === "") {
    return null;
  }
  return token;
};
export var buildConsentsUrl = baseUrl => {
  var normalizedBaseUrl = typeof baseUrl === "string" ? baseUrl.trim().replace(/\/+$/, "") : "";
  if (!normalizedBaseUrl) {
    throw new ConsentApiError("Consent API base URL is not configured", {
      code: CONSENT_ERROR.NOT_CONFIGURED
    });
  }
  return "".concat(normalizedBaseUrl).concat(CONSENTS_PATH);
};

/** A missing choice, or any value other than JSON `true`, is not granted. */
export var normalizeConsents = payload => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new ConsentApiError("Consent API returned an invalid body", {
      code: CONSENT_ERROR.INVALID_RESPONSE
    });
  }
  var consents = _objectSpread({}, EMPTY_CONSENTS);
  MANAGED_PURPOSES.forEach(purpose => {
    consents[purpose] = payload[purpose] === true;
  });
  return consents;
};

/** Fields left out of a PUT stay as they are on the server. */
export var diffConsents = (current, next) => {
  var body = {};
  MANAGED_PURPOSES.forEach(purpose => {
    var nextValue = Boolean(next && next[purpose]);
    var currentValue = Boolean(current && current[purpose]);
    if (nextValue !== currentValue) {
      body[purpose] = nextValue;
    }
  });
  return body;
};
var request = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator(function* (_ref) {
    var {
      baseUrl,
      method,
      body,
      storage,
      fetchImpl,
      path = CONSENTS_PATH
    } = _ref;
    var token = readCustomerToken(storage);
    if (!token) {
      throw new ConsentApiError("Missing Magento customer token", {
        status: 401,
        code: CONSENT_ERROR.MISSING_TOKEN
      });
    }
    var url = buildConsentsUrl(baseUrl).replace(CONSENTS_PATH, path);
    var doFetch = fetchImpl || (typeof fetch === "function" ? fetch : null);
    if (!doFetch) {
      throw new ConsentApiError("fetch is not available", {
        code: CONSENT_ERROR.UNREACHABLE
      });
    }
    var headers = {
      Accept: "application/json",
      Authorization: "Bearer ".concat(token)
    };
    if (body) {
      headers["Content-Type"] = "application/json";
    }
    var controller = typeof AbortController === "function" ? new AbortController() : null;
    var timeoutId = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
    var response;
    try {
      response = yield doFetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller ? controller.signal : undefined
      });
    } catch (error) {
      throw new ConsentApiError("Consent API is unreachable", {
        code: CONSENT_ERROR.UNREACHABLE,
        cause: error
      });
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
    if (!response || !response.ok) {
      var status = response ? response.status : null;
      throw new ConsentApiError("Consent API responded with status ".concat(status || "unknown"), {
        status,
        code: STATUS_CODES[status] || "http"
      });
    }
    var text;
    try {
      text = yield response.text();
    } catch (error) {
      throw new ConsentApiError("Consent API is unreachable", {
        status: response.status,
        code: CONSENT_ERROR.UNREACHABLE,
        cause: error
      });
    }
    if (!text) {
      throw new ConsentApiError("Consent API returned an empty body", {
        status: response.status,
        code: CONSENT_ERROR.INVALID_RESPONSE
      });
    }
    try {
      return JSON.parse(text);
    } catch (error) {
      throw new ConsentApiError("Consent API returned an invalid JSON body", {
        status: response.status,
        code: CONSENT_ERROR.INVALID_RESPONSE,
        cause: error
      });
    }
  });
  return function request(_x) {
    return _ref2.apply(this, arguments);
  };
}();
export var fetchCustomerConsents = /*#__PURE__*/function () {
  var _ref3 = _asyncToGenerator(function* () {
    var {
      baseUrl,
      storage,
      fetchImpl
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var payload = yield request({
      baseUrl,
      method: "GET",
      storage,
      fetchImpl
    });
    return normalizeConsents(payload);
  });
  return function fetchCustomerConsents() {
    return _ref3.apply(this, arguments);
  };
}();

/**
 * Sends a PUT only when at least one managed choice differs from `savedConsents`.
 * The body contains only those choices. Resolves with both current choices.
 */
export var saveCustomerConsents = /*#__PURE__*/function () {
  var _ref4 = _asyncToGenerator(function* () {
    var {
      baseUrl,
      consents,
      savedConsents,
      storage,
      fetchImpl
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var body = diffConsents(savedConsents, consents);
    if (Object.keys(body).length === 0) {
      return normalizeConsents(_objectSpread(_objectSpread({}, EMPTY_CONSENTS), savedConsents || {}));
    }
    var payload = yield request({
      baseUrl,
      method: "PUT",
      body,
      storage,
      fetchImpl
    });
    return normalizeConsents(payload);
  });
  return function saveCustomerConsents() {
    return _ref4.apply(this, arguments);
  };
}();

/** Capture explicit opt-ins. An unchecked box is not a withdrawal. */
export var grantCustomerConsents = /*#__PURE__*/function () {
  var _ref5 = _asyncToGenerator(function* () {
    var {
      baseUrl,
      consents,
      storage,
      fetchImpl
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var body = {};
    [...MANAGED_PURPOSES, "participation"].forEach(purpose => {
      if (consents && consents[purpose] === true) body[purpose] = true;
    });
    if (Object.keys(body).length === 0) return null;
    var result = yield request({
      baseUrl,
      method: "PUT",
      body,
      storage,
      fetchImpl
    });
    if (!result || Object.keys(body).some(purpose => result[purpose] !== true)) {
      throw new ConsentApiError("Consent API did not confirm the requested grants", {
        code: CONSENT_ERROR.INVALID_RESPONSE
      });
    }
    return normalizeConsents(result);
  });
  return function grantCustomerConsents() {
    return _ref5.apply(this, arguments);
  };
}();

/** OTP can register or log in: create a missing participation record without renewing one. */
export var ensureCustomerParticipation = /*#__PURE__*/function () {
  var _ref6 = _asyncToGenerator(function* () {
    var {
      baseUrl,
      storage,
      fetchImpl
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var payload = yield request({
      baseUrl,
      method: "POST",
      storage,
      fetchImpl,
      path: "/api/Customer/Participation"
    });
    if (!payload || typeof payload.participation !== "boolean") {
      throw new ConsentApiError("Participation API did not confirm a recorded state", {
        code: CONSENT_ERROR.INVALID_RESPONSE
      });
    }
    return payload;
  });
  return function ensureCustomerParticipation() {
    return _ref6.apply(this, arguments);
  };
}();